import { getToken, saveToken } from '../offline/db';
import { fetchWithTimeout } from './fetchWithTimeout';
import { phoneProof, type PhoneProof } from '../../platform/phoneKey';

/**
 * Keep a working driver signed in, without lengthening what a lost phone gets.
 *
 * The session is two hours of INACTIVITY. While a driver is on a route they
 * touch the server every few minutes, so this quietly renews and they sign in
 * once a day. A phone on a seat, lost, or handed over stops renewing and is
 * dead within two hours.
 *
 * Why it was needed: at one hour flat, every driver re-authenticated mid-route,
 * and a driver lost an entire day to an expired session whose uploads were
 * refused in silence. Measured from this app's own clocks, real shifts run two
 * to three hours of active work.
 *
 * The server does the deciding. This only ASKS, and only when the token is
 * already past halfway; an expired token, or one from a revoked device, is
 * refused there and the driver goes back to the sign-in screen — which is
 * exactly what should happen.
 */

const AUTH_BASE = import.meta.env.VITE_AUTH_BASE_URL as string;

/** Seconds left on a JWT, read from its own `exp`. No verification: the server
 *  does that. This only decides WHEN to ask, so a lie costs nothing. */
function secondsLeft(token: string): number | null {
  try {
    const body = token.split('.')[1];
    if (!body) return null;
    const json = JSON.parse(
      atob(body.replace(/-/g, '+').replace(/_/g, '/')),
    ) as { exp?: number };
    if (typeof json.exp !== 'number') return null;
    return json.exp - Math.floor(Date.now() / 1000);
  } catch {
    return null;
  }
}

/** This phone's key signs the session it renews (30 Oct), so a session copied
 *  to another phone cannot be kept alive there. */
async function refreshProof(token: string): Promise<PhoneProof | null> {
  try {
    const body = token.split('.')[1] ?? '';
    const { jti } = JSON.parse(atob(body.replace(/-/g, '+').replace(/_/g, '/'))) as { jti?: string };
    return jti ? await phoneProof(`refresh:${jti}`) : null;
  } catch {
    return null;
  }
}

let asking = false;

/**
 * Renew if the session is past halfway. Safe to call as often as you like.
 *
 * Silent about every failure on purpose: a refresh that cannot happen is not a
 * reason to interrupt a delivery. The session simply runs its course and the
 * ordinary expiry handling takes over.
 */
export async function keepSessionAlive(): Promise<void> {
  if (asking) return;
  try {
    const token = await getToken();
    if (!token) return;

    const left = secondsLeft(token);
    // Unreadable, or more than half the session remaining: nothing to do.
    if (left === null || left > 3600) return;
    // Already gone. Asking cannot help; the 401 path handles this properly.
    if (left <= 0) return;

    asking = true;
    const res = await fetchWithTimeout(`${AUTH_BASE}/session/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      /*
       * An explicit empty body, deliberately.
       *
       * The load balancer in front of these services answers 411 Length
       * Required to a POST that carries no Content-Length, and a bodyless
       * fetch does not reliably set one. Verified against the live endpoint:
       * without this the refresh never reaches the service at all, and the
       * failure would be invisible because this function swallows everything.
       */
      body: JSON.stringify({ phone: await refreshProof(token) }),
    });
    if (!res.ok) return;                       // revoked, expired, offline
    const body = (await res.json()) as { token?: string };
    if (body.token) await saveToken(body.token);
  } catch {
    /* Offline or refused. The session runs its course; nothing is broken. */
  } finally {
    asking = false;
  }
}
